import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiDelete, apiGet, apiPost, apiRequest } from '../utils/api';

interface RegistrationRequestItem {
  id: string;
  createdAt: string;
  telegramId: string;
  telegramUsername?: string | null;
  telegramFirstName?: string | null;
  telegramLastName?: string | null;
  interfaceLanguage: string;
  fullName: string;
  phoneNumber: string;
  singlesLevelSelected: string;
  singlesLevelComment?: string | null;
  status: 'PENDING_ACTIVATION' | 'ACTIVE' | 'REJECTED' | string;
  linkedPlayerId?: string | null;
  activatedAt?: string | null;
  adminComment?: string | null;
}

interface PlayerItem {
  id: string;
  name: string;
}

const statusOptions = [
  { value: '', label: 'Все статусы' },
  { value: 'PENDING_ACTIVATION', label: 'Ожидает активации' },
  { value: 'ACTIVE', label: 'Активирован' },
  { value: 'REJECTED', label: 'Отклонен' },
];

const TelegramRegistrationRequests: React.FC = () => {
  const [requests, setRequests] = useState<RegistrationRequestItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [savingId, setSavingId] = useState<string | null>(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<RegistrationRequestItem | null>(null);
  const [players, setPlayers] = useState<PlayerItem[]>([]);
  const [playersLoading, setPlayersLoading] = useState(false);
  const [playersSearch, setPlayersSearch] = useState('');
  const [playersSearchDebounced, setPlayersSearchDebounced] = useState('');
  const [playerAccountsMap, setPlayerAccountsMap] = useState<Record<string, any[]>>({});
  const [showNoIdOnly, setShowNoIdOnly] = useState(false);

  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      const params: Record<string, string> = {};
      if (statusFilter) params.status = statusFilter;
      const response = await apiGet<{ requests: RegistrationRequestItem[] }>('/registration-requests', params);
      setRequests(response.requests || []);
    } catch (error) {
      console.error('Ошибка загрузки заявок на регистрацию:', error);
      alert('Не удалось загрузить заявки на регистрацию');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setPlayersSearchDebounced(playersSearch.trim());
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [playersSearch]);

  useEffect(() => {
    if (!showAssignModal) return;
    const loadPlayers = async () => {
      try {
        setPlayersLoading(true);
        const params = new URLSearchParams();
        params.append('limit', '500');
        params.append('page', '1');
        params.append('isActive', 'true');
        if (playersSearchDebounced) {
          params.append('search', playersSearchDebounced);
        }
        const data = await apiGet<{ players: PlayerItem[] }>(`/players?${params.toString()}`);
        const loadedPlayers = data.players || [];
        setPlayers(loadedPlayers);

        try {
          const accountsData = await apiGet<{ accounts: any[] }>('/players/telegram-accounts/all');
          const allAccounts = Array.isArray(accountsData?.accounts) ? accountsData.accounts : [];
          const playerIdsSet = new Set(loadedPlayers.map((p) => p.id));
          const map: Record<string, any[]> = {};
          allAccounts.forEach((acc: any) => {
            const pid = acc?.playerId;
            if (!pid || !playerIdsSet.has(pid)) return;
            if (!map[pid]) map[pid] = [];
            map[pid].push(acc);
          });
          setPlayerAccountsMap(map);
        } catch (error) {
          console.error('Ошибка загрузки Telegram-аккаунтов:', error);
          setPlayerAccountsMap({});
        }
      } catch (error) {
        console.error('Ошибка загрузки игроков:', error);
      } finally {
        setPlayersLoading(false);
      }
    };
    loadPlayers();
  }, [showAssignModal, playersSearchDebounced]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return requests;
    return requests.filter((r) =>
      [
        r.fullName,
        r.phoneNumber,
        r.telegramId,
        r.telegramUsername || '',
        r.telegramFirstName || '',
        r.telegramLastName || '',
        r.singlesLevelSelected,
      ]
        .join(' ')
        .toLowerCase()
        .includes(query)
    );
  }, [requests, search]);

  const formatDate = (value?: string | null): string => {
    if (!value) return '-';
    try {
      return new Date(value).toLocaleString('ru-RU');
    } catch {
      return value;
    }
  };

  const statusLabel = (status: string): string => {
    if (status === 'PENDING_ACTIVATION') return 'Ожидает активации';
    if (status === 'ACTIVE') return 'Активирован';
    if (status === 'REJECTED') return 'Отклонен';
    return status;
  };

  const updateStatus = async (item: RegistrationRequestItem, status: 'ACTIVE' | 'REJECTED' | 'PENDING_ACTIVATION') => {
    try {
      setSavingId(item.id);
      const adminComment = status === 'REJECTED'
        ? prompt('Комментарий администратора (опционально):', item.adminComment || '') ?? ''
        : item.adminComment || '';

      const response = await apiRequest(`/registration-requests/${item.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          status,
          adminComment: adminComment || null,
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
        throw new Error(error.error || error.message || `HTTP ${response.status}`);
      }

      await fetchRequests();
    } catch (error: any) {
      console.error('Ошибка обновления статуса заявки:', error);
      alert(error?.message || 'Не удалось обновить статус заявки');
    } finally {
      setSavingId(null);
    }
  };

  const deleteRequest = async (item: RegistrationRequestItem) => {
    const ok = confirm(`Удалить заявку "${item.fullName}"?`);
    if (!ok) return;

    try {
      setSavingId(item.id);
      await apiDelete(`/registration-requests/${item.id}`);
      await fetchRequests();
    } catch (error: any) {
      console.error('Ошибка удаления заявки:', error);
      alert(error?.message || 'Не удалось удалить заявку');
    } finally {
      setSavingId(null);
    }
  };

  const playersForRender = useMemo(() => {
    if (!showNoIdOnly) return players;
    return players.filter((player) => {
      const accounts = playerAccountsMap[player.id] || [];
      return accounts.length === 0;
    });
  }, [players, playerAccountsMap, showNoIdOnly]);

  const handleRowClick = (item: RegistrationRequestItem) => {
    setSelectedRequest(item);
    setPlayersSearch('');
    setPlayersSearchDebounced('');
    setShowNoIdOnly(false);
    setShowAssignModal(true);
  };

  const handleAssignPlayer = async (playerId: string) => {
    if (!selectedRequest) return;
    try {
      setSavingId(selectedRequest.id);
      const existingAccounts = playerAccountsMap[playerId] || [];
      const isPrimary = existingAccounts.length === 0;
      await apiPost(`/players/${playerId}/telegram-accounts`, {
        chatId: selectedRequest.telegramId,
        username: selectedRequest.telegramUsername || undefined,
        firstName: selectedRequest.telegramFirstName || undefined,
        lastName: selectedRequest.telegramLastName || undefined,
        isPrimary,
      });

      const patchResponse = await apiRequest(`/registration-requests/${selectedRequest.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          linkedPlayerId: playerId,
          status: 'ACTIVE',
          adminComment: 'Активировано через привязку в заявках Telegram',
        }),
      });
      if (!patchResponse.ok) {
        const error = await patchResponse.json().catch(() => ({ error: 'Ошибка привязки заявки' }));
        throw new Error(error.error || error.message || 'Ошибка привязки заявки');
      }

      setShowAssignModal(false);
      setSelectedRequest(null);
      await fetchRequests();
    } catch (error: any) {
      console.error('Ошибка привязки игрока:', error);
      alert(error?.message || 'Не удалось привязать игрока');
    } finally {
      setSavingId(null);
    }
  };

  const handleCancelActivation = async (item: RegistrationRequestItem) => {
    if (!item.linkedPlayerId) {
      alert('У заявки нет привязанного игрока.');
      return;
    }
    const ok = confirm(`Отменить активацию и удалить TG ID у игрока для заявки "${item.fullName}"?`);
    if (!ok) return;

    try {
      setSavingId(item.id);

      const accountsResponse = await apiGet<{ accounts: Array<{ id: string }> }>(`/players/${item.linkedPlayerId}/telegram-accounts`);
      const accounts = accountsResponse?.accounts || [];

      for (const account of accounts) {
        await apiDelete(`/players/${item.linkedPlayerId}/telegram-accounts/${account.id}`);
      }

      const patchResponse = await apiRequest(`/registration-requests/${item.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          status: 'PENDING_ACTIVATION',
          linkedPlayerId: null,
          adminComment: 'Активация отменена: TG ID удален из карточки игрока',
        }),
      });
      if (!patchResponse.ok) {
        const error = await patchResponse.json().catch(() => ({ error: `HTTP ${patchResponse.status}` }));
        throw new Error(error.error || error.message || `HTTP ${patchResponse.status}`);
      }

      await fetchRequests();
    } catch (error: any) {
      console.error('Ошибка отмены активации:', error);
      alert(error?.message || 'Не удалось отменить активацию');
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Заявки на регистрацию</h1>
          <p className="mt-1 text-sm text-gray-600">Onboarding-заявки из Telegram-бота.</p>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="border rounded px-3 py-2 text-sm"
        >
          {statusOptions.map((option) => (
            <option key={option.value || 'all'} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Поиск: ФИО / телефон / Telegram"
          className="border rounded px-3 py-2 text-sm md:col-span-2"
        />
      </div>

      <div className="mt-4 bg-white shadow-sm ring-1 ring-gray-200 rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-6 text-sm text-gray-600">Загрузка...</div>
        ) : filtered.length === 0 ? (
          <div className="p-6 text-sm text-gray-600">Заявок не найдено.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">Дата</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">Игрок</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">Контакт</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">Уровень</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">Комментарий</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">Статус</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">Действия</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {filtered.map((item) => (
                  <tr
                    key={item.id}
                    className={item.status === 'ACTIVE' ? 'bg-white' : 'cursor-pointer hover:bg-gray-50'}
                    onClick={() => {
                      if (item.status !== 'ACTIVE') {
                        handleRowClick(item);
                      }
                    }}
                  >
                    <td className="px-4 py-3 whitespace-nowrap">{formatDate(item.createdAt)}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{item.fullName}</div>
                      <div className="text-xs text-gray-500">
                        TG: {item.telegramId}
                        {item.telegramUsername ? ` (@${item.telegramUsername})` : ''}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div>{item.phoneNumber}</div>
                      <div className="text-xs text-gray-500">Язык: {item.interfaceLanguage || 'RO'}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div>{item.singlesLevelSelected}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-gray-700 break-words max-w-[260px]">{item.singlesLevelComment || '-'}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700">
                        {statusLabel(item.status)}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex gap-2">
                        <button
                          disabled={savingId === item.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (item.status === 'ACTIVE') {
                              handleCancelActivation(item);
                            } else if (item.status === 'REJECTED') {
                              updateStatus(item, 'PENDING_ACTIVATION');
                            } else {
                              updateStatus(item, 'ACTIVE');
                            }
                          }}
                          className="px-1 py-1 text-sm disabled:opacity-50"
                          title={
                            item.status === 'ACTIVE'
                              ? 'Отменить активацию'
                              : item.status === 'REJECTED'
                                ? 'Отменить отклонение'
                                : 'Активировать'
                          }
                        >
                          {item.status === 'ACTIVE' || item.status === 'REJECTED' ? '↩️' : '✅'}
                        </button>
                        <button
                          disabled={savingId === item.id || item.status === 'REJECTED'}
                          onClick={(e) => {
                            e.stopPropagation();
                            updateStatus(item, 'REJECTED');
                          }}
                          className="px-1 py-1 text-sm disabled:opacity-50"
                          title="Отклонить"
                        >
                          ❌
                        </button>
                        <button
                          disabled={savingId === item.id || item.status === 'PENDING_ACTIVATION'}
                          onClick={(e) => {
                            e.stopPropagation();
                            updateStatus(item, 'PENDING_ACTIVATION');
                          }}
                          className="px-1 py-1 text-sm disabled:opacity-50"
                          title="В ожидание"
                        >
                          ⏳
                        </button>
                        <button
                          disabled={savingId === item.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteRequest(item);
                          }}
                          className="px-1 py-1 text-sm disabled:opacity-50"
                          title="Удалить"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showAssignModal && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[85vh] overflow-hidden">
            <div className="px-5 py-4 border-b">
              <h3 className="text-xl font-semibold text-gray-900">Присвоить Chat ID и username игроку</h3>
              <div className="text-sm text-gray-600 mt-1">
                <div>Имя: <span className="font-medium">{selectedRequest.fullName}</span></div>
                <div>Chat ID: <span className="font-medium">{selectedRequest.telegramId}</span></div>
              </div>
            </div>

            <div className="p-4 border-b">
              <div className="flex items-center gap-3">
                <input
                  type="text"
                  value={playersSearch}
                  onChange={(e) => setPlayersSearch(e.target.value)}
                  placeholder="Поиск игрока по имени..."
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
                <label className="inline-flex items-center gap-2 text-sm text-gray-700 whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={showNoIdOnly}
                    onChange={(e) => setShowNoIdOnly(e.target.checked)}
                    className="h-4 w-4"
                  />
                  no ID
                </label>
              </div>
              <div className="text-xs text-gray-500 mt-2">Найдено игроков: {playersForRender.length}</div>
            </div>

            <div className="max-h-[420px] overflow-y-auto p-4 space-y-2">
              {playersLoading ? (
                <div className="text-sm text-gray-600">Загрузка...</div>
              ) : playersForRender.length === 0 ? (
                <div className="text-sm text-gray-600">Игроки не найдены</div>
              ) : (
                playersForRender.map((player) => {
                  const accounts = playerAccountsMap[player.id] || [];
                  return (
                    <div key={player.id} className="border rounded-lg px-3 py-2 flex items-center justify-between">
                      <div>
                        <div className="font-medium text-gray-900">{player.name}</div>
                        {accounts.length > 0 && (
                          <div className="text-xs text-orange-600">
                            Уже есть {accounts.length} Telegram аккаунт{accounts.length > 1 ? 'ов' : ''}:
                            {' '}
                            {accounts.map((acc) => `${acc.chatId}${acc.isPrimary ? ' (основной)' : ''}`).join(', ')}
                          </div>
                        )}
                      </div>
                      <button
                        onClick={() => handleAssignPlayer(player.id)}
                        className="px-3 py-1.5 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                        disabled={savingId === selectedRequest.id}
                      >
                        Выбрать
                      </button>
                    </div>
                  );
                })
              )}
            </div>

            <div className="px-4 py-3 border-t flex justify-end">
              <button
                onClick={() => {
                  setShowAssignModal(false);
                  setSelectedRequest(null);
                }}
                className="px-4 py-2 rounded bg-gray-100 text-gray-700 hover:bg-gray-200"
              >
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TelegramRegistrationRequests;
